// One record, its fields, and the child lists that hang off it.
//
// The related lists are the source's: every reference row shows its barriers,
// an order shows its barriers and both production tables, a product shows its
// orders as well. Each is the collection endpoint with one equality filter.

import { useMemo } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import * as api from '../api';
import { COLLECTIONS, REFERENCES } from '../schema';
import { useAsync, useCollection, useLookups, useMe } from '../data';
import { renderValue } from '../grid/DataGrid';
import { actionOwner, enteredBy } from '../format';
import { landingFor, mayUseJobMenu } from '../shell/JobMenu';
import { ErrorNote } from '../shell/Screen';
import { RELATED } from './related';
import type { Row } from '../types';

export function RecordScreen() {
  const { slug = '', id = '' } = useParams();
  const navigate = useNavigate();
  const collection = COLLECTIONS[slug];
  const record = useAsync(() => api.getRow(slug, id), [slug, id]);
  const me = useMe();
  const references = useMemo(
    () => (collection ? collection.fields.map((f) => f.name).filter((n) => REFERENCES[n]) : []),
    [collection]
  );
  const lookups = useLookups(references);

  if (!collection) return <ErrorNote error={new Error(`There is no collection named ${slug}.`)} />;
  if (record.error) return <ErrorNote error={record.error} />;
  if (record.loading || !record.data) return <p className="muted">Loading…</p>;

  const row = record.data;
  // Hiding a control the caller may not use is a courtesy, not the control:
  // every write path checks the role again, server-side.
  const mayWrite = collection.writeRole === 'User' || Boolean(me.data?.is_admin);
  // A table's list is behind the top menu (#43). Somebody who reached this
  // record from the rail is not pointed at one: the table's name is not a link,
  // and a delete takes them to where the application opens for them.
  const mayList = mayUseJobMenu(me.data);

  async function remove() {
    if (!window.confirm('Delete this record? This cannot be undone.')) return;
    await api.deleteRow(slug, id);
    navigate(mayList ? `/data/${slug}` : landingFor(me.data));
  }

  return (
    <section className="screen">
      <header className="screen-head">
        <div>
          <h1>
            {singularOf(collection.label)} {String(row[collection.pk])}
          </h1>
          <p className="screen-description">
            {mayList ? <Link to={`/data/${slug}`}>{collection.label}</Link> : collection.label}
          </p>
        </div>
        <div className="screen-tools">
          {mayWrite && (
            <>
              <button className="primary" onClick={() => navigate(`/data/${slug}/${id}/edit`)}>
                Edit
              </button>
              {slug === 'barriers' && (
                <button onClick={() => navigate(`/data/barriers/new?duplicate=${id}`)}>Duplicate</button>
              )}
              <button className="danger" onClick={remove}>
                Delete
              </button>
            </>
          )}
        </div>
      </header>

      <dl className="record">
        {collection.fields
          // The directory's keys for who raised a barrier, who acts on it and
          // who last changed Hot: what survives a rename, and GUIDs nobody
          // reads. The name beside each says who.
          .filter(
            (field) =>
              !['entered_by_object_id', 'action_owner_object_id', 'hot_changed_by_object_id'].includes(field.name)
          )
          .map((field) => {
            const reference = REFERENCES[field.name];
            const value = row[field.name];
            const resolved =
              reference && typeof value === 'number' ? lookups.data?.[field.name]?.get(value) : undefined;
            return (
              <div key={field.name} className="record-field">
                <dt>{field.label}</dt>
                <dd>
                  {resolved !== undefined ? (
                    <Link to={`/data/${reference!.collection}/${value}`}>{resolved}</Link>
                  ) : field.name === 'entered_by_name' ? (
                    enteredBy(row) || '—'
                  ) : field.name === 'action_owner' ? (
                    actionOwner(row) || '—'
                  ) : (
                    renderValue(value, field.type) || '—'
                  )}
                </dd>
              </div>
            );
          })}
      </dl>

      {(RELATED[slug] ?? []).map((related) => (
        <RelatedRows key={`${related.slug}:${related.column}`} slug={related.slug} column={related.column} label={related.label} value={row[collection.pk]} />
      ))}
    </section>
  );
}

function RelatedRows({ slug, column, label, value }: { slug: string; column: string; label: string; value: unknown }) {
  const collection = COLLECTIONS[slug];
  const rows = useCollection(slug, { [column]: value as string | number });
  const columns = collection.fields.filter((f) => f.writable && f.name !== collection.pk).slice(0, 6);

  return (
    <section className="related">
      <h2>
        {label} <span className="muted">({rows.data?.length ?? 0})</span>
      </h2>
      {rows.error && <ErrorNote error={rows.error} />}
      {rows.loading ? (
        <p className="muted">Loading…</p>
      ) : (rows.data ?? []).length === 0 ? (
        <p className="muted">None.</p>
      ) : (
        <table className="related-table">
          <thead>
            <tr>
              <th>{collection.pk}</th>
              {columns.map((field) => (
                <th key={field.name}>{field.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(rows.data ?? []).map((related: Row) => (
              <tr key={String(related[collection.pk])}>
                <td>
                  <Link to={`/data/${slug}/${related[collection.pk]}`}>{String(related[collection.pk])}</Link>
                </td>
                {columns.map((field) => (
                  <td key={field.name}>{renderValue(related[field.name], field.type)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

function singularOf(label: string): string {
  if (label.endsWith('ies')) return `${label.slice(0, -3)}y`;
  if (label.endsWith('s')) return label.slice(0, -1);
  return label;
}
